import {
  ColumnDef,
  ColumnHelper,
  FilterFn,
  Header,
  Row as TanTableRow,
  SortingFn,
} from "@tanstack/react-table";
import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import { useAuthenticatedFetch } from "../../lib/api";
import MultiSelector from "../MultiSelector";

// ── Service Tier (Slant) — shared, generic column / sorting / two-level filter ─
// s-slant-client-tier-as-019ec6dd (R5/R6/R7). A `Household` (APP-1648) and a
// `RebalanceWithMetrics` (APP-1652) always carry the `serviceTier` key, `null`
// when there is no tier, so a row cannot say whether the user is connected to
// Slant. The household list and the four rebalance tables read
// `connections.slant` from GET /profile instead (`useSlantConnected` below).
// `hasSlantConnection` reads `serviceTier !== undefined`, which is true of
// every such row; no table calls it.
//
// Generic over any row type that exposes a `serviceTier` field, mirroring how
// the other filters in `components/Table/filters.tsx` are generic. The two
// consumers — the household list (J4) and the four rebalance tables (J6) —
// share THIS one implementation via `makeServiceTierColumn`.

export const UNTIERED_LABEL = "Untiered / None";

/** The shape of a single row's serviceTier value (object form). */
export type ServiceTier = {
  bookId: string | null;
  bookName: string | null;
  label: string;
  value: number | null;
};

/** Minimal row contract the Service Tier logic reads. */
type ServiceTierRow = { serviceTier?: ServiceTier | null };

export type ServiceTierFilterValue = {
  bookId: string | null;
  /** Selected tier labels within the book. */
  tiers: string[];
  /** Whether the "Untiered / None" bucket is selected. */
  includeUntiered: boolean;
};

/**
 * Whether the user is connected to Slant (`connections.slant` on GET
 * /profile), which decides whether the Service Tier column shows on the
 * rebalance tables. `false` until the profile has loaded.
 */
export const useSlantConnected = () => {
  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  return profileBody?.data.connections.slant === true;
};

/**
 * True when at least one loaded row has the serviceTier key (firm is
 * Slant-connected). Accepts any row array; rows missing the field read as
 * undefined. The broad parameter avoids TS weak-type rejection when callers
 * pass a richer row type (e.g. RebalanceRow) whose only shared member with
 * ServiceTierRow is the optional `serviceTier`.
 */
export const hasSlantConnection = (
  rows: ReadonlyArray<{ serviceTier?: ServiceTier | null } | object>,
): boolean => rows.some((r) => (r as ServiceTierRow).serviceTier !== undefined);

/**
 * Sort by serviceTier.value ASC (top tier first; value 0 first), NULLS LAST.
 * Rows with a null/absent value always sort after rows with a present value,
 * regardless of sort direction (nulls-last on the natural ascending order).
 */
export const serviceTierSortingFn: SortingFn<unknown> = (rowA, rowB) => {
  const a = (rowA.original as ServiceTierRow).serviceTier?.value ?? null;
  const b = (rowB.original as ServiceTierRow).serviceTier?.value ?? null;
  if (a === null && b === null) return 0;
  if (a === null) return 1; // push nulls down
  if (b === null) return -1;
  return a - b;
};

/** Distinct books across rows' serviceTier, keyed by bookId. */
export const deriveServiceTierBooks = (
  rows: ReadonlyArray<ServiceTierRow>,
): Array<{ bookId: string | null; bookName: string | null }> => {
  const byId = new Map<
    string | null,
    { bookId: string | null; bookName: string | null }
  >();
  for (const r of rows) {
    const tier = r.serviceTier;
    if (!tier) continue; // null/undefined carries no book
    if (!byId.has(tier.bookId)) {
      byId.set(tier.bookId, { bookId: tier.bookId, bookName: tier.bookName });
    }
  }
  return [...byId.values()];
};

/**
 * Distinct tier labels for the given book, ordered by value ASC, absent-value
 * labels last. Only rows whose serviceTier belongs to `bookId` contribute.
 */
export const deriveTierOptions = (
  rows: ReadonlyArray<ServiceTierRow>,
  bookId: string | null,
): string[] => {
  const byLabel = new Map<string, number | null>();
  for (const r of rows) {
    const tier = r.serviceTier;
    if (!tier || tier.bookId !== bookId) continue;
    if (!byLabel.has(tier.label)) byLabel.set(tier.label, tier.value);
  }
  return [...byLabel.entries()]
    .sort(([, av], [, bv]) => {
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return av - bv;
    })
    .map(([label]) => label);
};

/**
 * Row passes when its book matches the selected book AND (its tier label is in
 * the selected set, OR "Untiered / None" is selected and the row is untiered).
 * An empty selection (no tiers, no untiered) is a no-op pass.
 */
export const serviceTierFilterFn: FilterFn<unknown> = (
  row: TanTableRow<unknown>,
  _columnId: string,
  filterValue: ServiceTierFilterValue | undefined,
) => {
  if (!filterValue) return true;
  const { bookId, tiers, includeUntiered } = filterValue;
  if ((tiers?.length ?? 0) <= 0 && !includeUntiered) return true;

  const tier = (row.original as ServiceTierRow).serviceTier;
  // "Untiered / None" means the household carries no tier at all (Prospect or
  // untiered Client) — i.e. serviceTier is null. A household whose label is
  // present but absent from its book catalog (value === null, the J2 Case-3)
  // is still a *labelled* row: it renders its label, is offered as a filter
  // option, and must be reachable by selecting that label — so it is NOT
  // untiered here.
  const rowIsUntiered = tier == null;
  const rowBookId = tier?.bookId ?? null;

  if (rowIsUntiered) {
    return includeUntiered;
  }
  // Tiered row (incl. value===null catalog-absent labels): book must match the
  // selected book, label must be selected.
  return rowBookId === bookId && tiers.includes(tier!.label);
};

export const ServiceTierFilter = <TRow, TValue>({
  header,
}: {
  header: Header<TRow, TValue>;
}) => {
  const rows = header
    .getContext()
    .table.getPreFilteredRowModel()
    .rows.map((r) => r.original as unknown as ServiceTierRow);

  const books = useMemo(() => deriveServiceTierBooks(rows), [rows]);
  const singleBook = books.length === 1;

  const current = (header.column.getFilterValue() ?? undefined) as
    ServiceTierFilterValue | undefined;

  // Auto-select the only book; collapse the Level-1 selector.
  const selectedBookId = singleBook
    ? books[0].bookId
    : (current?.bookId ?? null);
  const bookChosen = singleBook || current?.bookId !== undefined;

  useEffect(() => {
    if (books.length > 1) {
      // Spec: a connection SHALL warn on >1 book.
      console.warn(
        `ServiceTierFilter: more than one Slant book on the list (${books.length}); expected exactly one.`,
      );
    }
  }, [books.length]);

  const tierLabels = useMemo(
    () => deriveTierOptions(rows, selectedBookId),
    [rows, selectedBookId],
  );

  const tierOptions = useMemo(
    () =>
      // Level 2 is disabled (no options) until a book is chosen.
      bookChosen
        ? [
            ...tierLabels.map((label) => ({ label, value: label })),
            { label: UNTIERED_LABEL, value: UNTIERED_LABEL },
          ]
        : [],
    [bookChosen, tierLabels],
  );

  const onBookChange = useCallback(
    (ev: ChangeEvent<HTMLSelectElement>) => {
      const bookId =
        ev.currentTarget.value === "" ? null : ev.currentTarget.value;
      header.column.setFilterValue(
        ev.currentTarget.value === ""
          ? undefined
          : { bookId, tiers: [], includeUntiered: false },
      );
    },
    [header.column],
  );

  const onTiersChange = useCallback(
    (values: string[]) => {
      const includeUntiered = values.includes(UNTIERED_LABEL);
      const tiers = values.filter((v) => v !== UNTIERED_LABEL);
      if (tiers.length <= 0 && !includeUntiered) {
        header.column.setFilterValue(undefined);
        return;
      }
      header.column.setFilterValue({
        bookId: selectedBookId,
        tiers,
        includeUntiered,
      });
    },
    [header.column, selectedBookId],
  );

  const tierValue = useMemo(() => {
    const vals = [...(current?.tiers ?? [])];
    if (current?.includeUntiered) vals.push(UNTIERED_LABEL);
    return vals;
  }, [current]);

  return (
    <>
      {!singleBook && (
        <Form.Select
          value={current?.bookId ?? ""}
          onChange={onBookChange}
          className="mb-1"
          aria-label="Book"
        >
          <option value="">All books</option>
          {books.map((book) => (
            <option key={book.bookId ?? "null"} value={book.bookId ?? ""}>
              {book.bookName ?? book.bookId ?? "(unnamed book)"}
            </option>
          ))}
        </Form.Select>
      )}
      <MultiSelector
        options={tierOptions}
        placeholder="All"
        setValue={onTiersChange}
        value={tierValue}
      />
    </>
  );
};

/**
 * Factory for the Service Tier column. Each table builds its own column helper
 * over its own row type (`createColumnHelper<RowType>()`), so the column itself
 * must be built per-table — but the accessor, cell, sorting, filter, and filter
 * component are all the shared generic implementations above.
 *
 * The row type `T` must expose `serviceTier`; the accessor reads it off
 * `row.serviceTier`.
 */
export function makeServiceTierColumn<T>(
  columnHelper: ColumnHelper<T>,
): ColumnDef<T, string> {
  return columnHelper.accessor(
    (row) => (row as unknown as ServiceTierRow).serviceTier?.label ?? "",
    {
      id: "serviceTier",
      cell: (info) => {
        const tier = (info.row.original as unknown as ServiceTierRow)
          .serviceTier;
        return tier?.label ?? "—";
      },
      header: () => "Service Tier",
      minSize: 120,
      sortingFn: serviceTierSortingFn as SortingFn<T>,
      filterFn: serviceTierFilterFn as FilterFn<T>,
      meta: {
        filterComponent: ServiceTierFilter,
      },
    },
  ) as ColumnDef<T, string>;
}
